import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getLoginToken, getStoredUser, setStoredUser } from "../storage";
import { useAuthenticatedUser } from "./hooks";
import { ChildProps, userProps } from "../interface";
import SessionTimeoutModal from "../modules/Timeout";

export interface AuthContextType {
  user?: userProps;
  token?: string;
  isAuthenticated: boolean;
  authenticate(token: string): void;
  logout(reason?: string, meta?: Record<string, any>): void;
  updateUser(data: userProps): void;
}

export const AuthContext = createContext<AuthContextType>({
  user: undefined,
  token: undefined,
  isAuthenticated: false,
  authenticate: () => {},
  logout: () => {},
  updateUser: () => {},
});

const TIMEOUT_WARNING_SECONDS = 60;
const SESSION_CHECK_INTERVAL_MS = 15 * 1000;
const DEBUG_AUTH = true;

type DecodedToken = {
  exp?: number;
  _id?: string;
  FirstName?: string;
  LastName?: string;
  PhoneNumber?: string;
  email?: string;
  role?: string;
  IsVerified?: boolean;
  fullname?: string;
};

function authLog(message: string, data?: Record<string, any>) {
  if (!DEBUG_AUTH) return;

  console.log(
    `%c[AUTH DEBUG] ${message}`,
    "color:#ff4b2b;font-weight:700;",
    data || "",
  );
}

function authWarn(message: string, data?: Record<string, any>) {
  if (!DEBUG_AUTH) return;

  console.warn(
    `%c[AUTH DEBUG] ${message}`,
    "color:#ff4b2b;font-weight:900;",
    data || "",
  );
}

function decodeJWT(token?: string): DecodedToken | null {
  if (!token) return null;

  try {
    const [, payload] = token.split(".");

    if (!payload) return null;

    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");

    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split("")
        .map((char) => {
          return `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`;
        })
        .join(""),
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    authWarn("JWT decode failed", { error });
    return null;
  }
}

function normalizeExpiry(exp?: number) {
  if (!exp) return undefined;

  /**
   * JWT exp is usually in seconds.
   * This also supports mistakenly stored milliseconds.
   */
  return exp > 9999999999 ? Math.floor(exp / 1000) : exp;
}

function AuthContextProvider({ children }: ChildProps) {
  const [authToken, setAuthToken] = useState<string | undefined>(() => {
    return getLoginToken() || undefined;
  });

  const [user, setUser] = useState<userProps | undefined>(() => {
    return getStoredUser() || undefined;
  });

  const [showTimeoutModal, setShowTimeoutModal] = useState(false);
  const [modalSecondsLeft, setModalSecondsLeft] = useState(
    TIMEOUT_WARNING_SECONDS,
  );

  const queryClient = useQueryClient();
  const userDetails = useAuthenticatedUser();

  const sessionIntervalRef = useRef<number | undefined>();
  const logoutTriggered = useRef(false);

  const clearSessionMonitor = useCallback(() => {
    if (sessionIntervalRef.current) {
      window.clearInterval(sessionIntervalRef.current);
      sessionIntervalRef.current = undefined;
    }
  }, []);

  const getTokenExpiry = useCallback((token?: string) => {
    const decoded = decodeJWT(token);
    const expiry = normalizeExpiry(decoded?.exp);
    const now = Math.floor(Date.now() / 1000);

    if (!expiry) {
      authWarn("Token has no usable exp value", { decoded });

      return {
        hasExpiry: false,
        expiresIn: 0,
        decoded,
      };
    }

    const expiresIn = expiry - now;

    authLog("Token expiry checked", {
      expiry,
      now,
      expiresIn,
      expiresAt: new Date(expiry * 1000).toLocaleString(),
    });

    return {
      hasExpiry: true,
      expiresIn,
      decoded,
    };
  }, []);

  const logout = useCallback(
    (reason = "unknown", meta: Record<string, any> = {}) => {
      authWarn("LOGOUT TRIGGERED", {
        reason,
        meta,
        hasAuthToken: Boolean(authToken),
        hasUser: Boolean(user),
        userEmail: user?.email,
        currentPath: window.location.pathname,
      });

      console.trace(`[AUTH DEBUG] logout() stack trace. Reason: ${reason}`);

      if (logoutTriggered.current) return;

      logoutTriggered.current = true;

      clearSessionMonitor();
      setShowTimeoutModal(false);
      setModalSecondsLeft(TIMEOUT_WARNING_SECONDS);

      localStorage.clear();
      setAuthToken(undefined);
      setUser(undefined);
      queryClient.clear();
    },
    [authToken, user, clearSessionMonitor, queryClient],
  );

  const checkSessionExpiry = useCallback(
    (token?: string) => {
      if (!token) {
        setShowTimeoutModal(false);
        return;
      }

      const { hasExpiry, expiresIn } = getTokenExpiry(token);

      /**
       * Do not logout if exp is missing.
       * Some tokens may not expose exp correctly.
       */
      if (!hasExpiry) {
        setShowTimeoutModal(false);
        return;
      }

      if (expiresIn <= 0) {
        logout("token_expired_from_interval_check", { expiresIn });
        return;
      }

      /**
       * Only show modal when session is actually about to expire.
       */
      if (expiresIn <= TIMEOUT_WARNING_SECONDS) {
        setModalSecondsLeft(Math.max(1, Math.ceil(expiresIn)));
        setShowTimeoutModal(true);
        return;
      }

      /**
       * Token is still valid and not close to expiry.
       */
      setShowTimeoutModal(false);
      setModalSecondsLeft(TIMEOUT_WARNING_SECONDS);
    },
    [getTokenExpiry, logout],
  );

  const startSessionMonitor = useCallback(
    (token?: string) => {
      clearSessionMonitor();
      setShowTimeoutModal(false);

      if (!token) return;

      /**
       * Run once immediately, then continue checking.
       * This avoids unsafe long setTimeout values.
       */
      checkSessionExpiry(token);

      sessionIntervalRef.current = window.setInterval(() => {
        checkSessionExpiry(token);
      }, SESSION_CHECK_INTERVAL_MS);
    },
    [clearSessionMonitor, checkSessionExpiry],
  );

  useEffect(() => {
    logoutTriggered.current = false;

    if (!authToken) {
      clearSessionMonitor();
      setShowTimeoutModal(false);
      return;
    }

    startSessionMonitor(authToken);

    return () => {
      clearSessionMonitor();
    };
  }, [authToken, clearSessionMonitor, startSessionMonitor]);

  useEffect(() => {
    if (!userDetails) return;

    setUser(userDetails);
    setStoredUser(userDetails);
  }, [userDetails]);

  function updateUser(data: userProps) {
    setUser(data);
    setStoredUser(data);
  }

  function authenticate(token: string) {
    logoutTriggered.current = false;

    clearSessionMonitor();
    setShowTimeoutModal(false);
    setModalSecondsLeft(TIMEOUT_WARNING_SECONDS);

    setAuthToken(token);

    const decoded = decodeJWT(token);

    const authenticatedUser: userProps = {
      _id: decoded?._id || "",
      firstname: decoded?.FirstName || "",
      lastname: decoded?.LastName || "",
      middlename: "",
      fullname:
        decoded?.fullname ||
        `${decoded?.FirstName || ""} ${decoded?.LastName || ""}`.trim(),
      phone: decoded?.PhoneNumber || "",
      email: decoded?.email || "",
      role: decoded?.role || "",
      isVerified: decoded?.IsVerified || false,
    };

    setUser(authenticatedUser);
    setStoredUser(authenticatedUser);

    startSessionMonitor(token);
  }

  const handleModalLogout = () => {
    setShowTimeoutModal(false);
    logout("user_clicked_timeout_modal_logout");
  };

  const handleExtend = () => {
    setShowTimeoutModal(false);

    /**
     * This only hides the modal and keeps monitoring the current token.
     * If your backend has a refresh-token endpoint, call it here,
     * then call authenticate(newToken).
     */
    checkSessionExpiry(authToken);
  };

  const value: AuthContextType = useMemo(
    () => ({
      user,
      token: authToken,
      isAuthenticated: Boolean(authToken),
      authenticate,
      logout,
      updateUser,
    }),
    [user, authToken, logout],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}

      <SessionTimeoutModal
        visible={showTimeoutModal}
        secondsLeft={modalSecondsLeft}
        onExtend={handleExtend}
        onLogout={handleModalLogout}
      />
    </AuthContext.Provider>
  );
}

export default AuthContextProvider;
